import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Blok } from '../../interfaces/Blok.interface';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import { Theme } from '../../interfaces/enums/Theme.enum';
import Heading, { HeadingTypes } from '../Heading/Heading';
import Image from '../Image/Image';
import Grid from '../Utils/Grid/Grid';
import Col from '../Utils/Col/Col';
import { ButtonSize } from '../../interfaces/enums/ButtonSize.enum';
import { StoryblokComponent } from 'gatsby-source-storyblok';
import SbImage from '../SbImage/SbImage';
import Container from '../Utils/Container/Container';
import algoliasearch from 'algoliasearch';
import { getQueryParam } from '../../utils/getQueryParam/getQueryParam.util';
import { GroupedArticleIndexPageResultsBlokProps } from '../GroupedArticleIndexPageResults/GroupedArticleIndexPageResults';
import { FlatArticleIndexPageResultsBlokProps } from '../FlatArticleIndexPageResults/FlatArticleIndexPageResults';
import { StoryblokNode } from '../../interfaces/StoryblokNode.interface';
import { StoryblokArticleAuthor } from '../../interfaces/StoryblokArticleAuthor.interface';
import {
  Configure,
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-hooks-web';
import AuthorArticlesResults from './AuthorArticlesResults';
import Subtitle from '../Subtitle/Subtitle';
import { AuthorContext } from '../../contexts/AuthorContext';

interface AuthorArticlesBlokProps extends Blok {
  heading: string;
  overline: string;
  applicable_author: StoryblokNode<StoryblokArticleAuthor>;
  searchable_category_tags?: string[];
  number_of_results_to_display?: number;
}

interface AuthorArticlesProps {
  blok: AuthorArticlesBlokProps;
}

const AuthorArticles: React.FC<AuthorArticlesProps> = ({ blok }) => {
  const {
    heading,
    overline,
    applicable_author,
    searchable_category_tags = [],
    number_of_results_to_display = 9,
  } = blok;

  // const { articlesPublished, setArticlesPublished } = useContext(AuthorContext);

  const indexName: string = useMemo(() => {
    return `${process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME}`;
  }, [process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME]);

  const algoliaSearchClient = useMemo(() => {
    return algoliasearch(
      `${process.env.GATSBY_ALGOLIA_APP_ID}`,
      `${process.env.GATSBY_ALGOLIA_SEARCH_API_KEY}`
    );
  }, [
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_SEARCH_API_KEY,
  ]);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const filters = useMemo(() => {
    const baseFilters: string = 'field_component:article';
    const authors = `authors:"${applicable_author.name}"`;
    if (!searchable_category_tags.length) return baseFilters;
    return `${baseFilters} AND ${authors} AND ${
      searchable_category_tags.length > 1 ? '(' : ''
    }${searchable_category_tags
      .map(
        (searchableCategoryTag: string) =>
          `category_tags:"${searchableCategoryTag}"`
      )
      .join(' OR ')}${searchable_category_tags.length > 1 ? ')' : ''}`;
  }, [searchable_category_tags]);

  const getInitialQuery = (): string => {
    if (typeof window === 'undefined') return '';
    return getQueryParam('q') || '';
  };

  const getInitialPage = (): number => {
    if (typeof window === 'undefined') return 0;
    return parseInt(getQueryParam('page')!) || 0;
  };

  /*-- useEffects --*/
  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 400);
  }, []);

  return (
    <section className="relative overflow-hidden pt-14 sm:pt-20 md:pt-28">
      <div className="text-center">
        {overline && (
          <Subtitle text={overline} className="pb-3 font-dm-serif-display" />
        )}
        {heading && <Heading text={heading} headingType={HeadingTypes.h2} />}
      </div>
      <InstantSearchSSRProvider>
        <InstantSearch
          indexName={indexName}
          searchClient={algoliaSearchClient}
          initialUiState={{
            [indexName]: {
              query: getInitialQuery(),
              page: getInitialPage(),
            },
          }}
        >
          <Configure
            filters={filters}
            hitsPerPage={number_of_results_to_display || 9}
          />
          <AuthorArticlesResults blok={{ isLoading: isLoading }} />
        </InstantSearch>
      </InstantSearchSSRProvider>
    </section>
  );
};

export default memo(withBlokWrapper(AuthorArticles));
